// ============================================================================
// ID GENERATION
// ============================================================================

let counter = 0;

export function generateId(): string {
  return `cmd_${Date.now()}_${++counter}`;
}

// ============================================================================
// SEARCH UTILITIES
// ============================================================================

export function normalizeSearchTerm(term: string): string {
  return term.toLowerCase().trim();
}

export function calculateSearchScore(
  command: any,
  queryTerms: string[],
): number {
  let score = 0;
  const searchableText = [
    command.label,
    command.description || "",
    ...(command.tags || []),
    ...(command.searchKeywords || []),
  ]
    .join(" ")
    .toLowerCase();

  queryTerms.forEach((term) => {
    // Exact match in label = highest score
    if (command.label.toLowerCase().includes(term)) {
      score += command.label.toLowerCase() === term ? 100 : 50;
    }

    // Match in description
    if (command.description?.toLowerCase().includes(term)) {
      score += 25;
    }

    // Match in tags
    if (command.tags?.some((tag: string) => tag.toLowerCase().includes(term))) {
      score += 15;
    }

    // Match in keywords
    if (
      command.searchKeywords?.some((keyword: string) =>
        keyword.toLowerCase().includes(term),
      )
    ) {
      score += 10;
    }

    // Fuzzy match
    if (searchableText.includes(term)) {
      score += 5;
    }
  });

  return score;
}

export function getMatchedTerms(command: any, queryTerms: string[]): string[] {
  const matched: string[] = [];
  const searchableText = [
    command.label,
    command.description || "",
    ...(command.tags || []),
    ...(command.searchKeywords || []),
  ]
    .join(" ")
    .toLowerCase();

  queryTerms.forEach((term) => {
    if (searchableText.includes(term)) {
      matched.push(term);
    }
  });

  return matched;
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

export function isValidCommandKey(key: any): key is string {
  return typeof key === "string" && key.length > 0;
}

export function isValidCommand(command: any): boolean {
  return (
    command &&
    typeof command === "object" &&
    isValidCommandKey(command.key) &&
    typeof command.label === "string" &&
    command.label.length > 0 &&
    typeof command.handle === "function"
  );
}

// ============================================================================
// ASYNC UTILITIES
// ============================================================================

export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string = "Operation timed out",
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(errorMessage));
      }, timeoutMs);
    }),
  ]);
}

export async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ============================================================================
// DEBOUNCE UTILITY
// ============================================================================

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate: boolean = false,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | undefined;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = undefined;
      if (!immediate) func(...args);
    };

    const callNow = immediate && !timeout;

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func(...args);
  };
}

// ============================================================================
// ARRAY UTILITIES
// ============================================================================

export function unique<T>(array: T[]): T[] {
  return [...new Set(array)];
}

export function removeItem<T>(array: T[], item: T): T[] {
  const index = array.indexOf(item);
  if (index > -1) {
    array.splice(index, 1);
  }
  return array;
}

// ============================================================================
// OBJECT UTILITIES
// ============================================================================

export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as T;
  }

  if (obj instanceof Array) {
    return obj.map((item) => deepClone(item)) as T;
  }

  if (typeof obj === "object") {
    const cloned = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = deepClone(obj[key]);
      }
    }
    return cloned;
  }

  return obj;
}

// ============================================================================
// PRIORITY QUEUE IMPLEMENTATION
// ============================================================================

export class PriorityQueue<T> {
  private items: Array<{ item: T; priority: number }> = [];

  enqueue(item: T, priority: number = 0): void {
    const queueItem = { item, priority };
    let added = false;

    for (let i = 0; i < this.items.length; i++) {
      if (queueItem.priority > this.items[i].priority) {
        this.items.splice(i, 0, queueItem);
        added = true;
        break;
      }
    }

    if (!added) {
      this.items.push(queueItem);
    }
  }

  dequeue(): T | undefined {
    return this.items.shift()?.item;
  }

  peek(): T | undefined {
    return this.items[0]?.item;
  }

  isEmpty(): boolean {
    return this.items.length === 0;
  }

  size(): number {
    return this.items.length;
  }

  clear(): void {
    this.items = [];
  }

  remove(predicate: (item: T) => boolean): boolean {
    const index = this.items.findIndex((queueItem) =>
      predicate(queueItem.item),
    );
    if (index >= 0) {
      this.items.splice(index, 1);
      return true;
    }
    return false;
  }
}
